import { useQueryClient } from "@tanstack/react-query"
import { useAsync } from "@mylaunch/components/hooks"
import { Result } from "@praha/byethrow"
import { toast } from "sonner"
import { ImSpinner2 } from "react-icons/im"
import { Button } from "~/components/ui/button"
import { usePreOpenSystemBrowser } from "../hooks/use-pre-open-system-browser"
import { plansQueryKey } from "../hooks/use-plans"
import { subscribe } from "../server/plans.functions"

interface Props extends Omit<React.ComponentProps<typeof Button>, "onClick"> {
  planId: number
  period: "monthly" | "yearly"
}

export function SubscribeButton({ planId, period, onMouseDown, onTouchStart, disabled, children, ...props }: Props) {
  const queryClient = useQueryClient()
  const browser = usePreOpenSystemBrowser()

  const { loading, run } = useAsync(async () => {
    const returnUrl = `${location.origin}/?tab=profile&pay=return`
    const result = await subscribe({ data: { planId, period, returnUrl } })
    return result
  })

  const handlePreOpen = () => {
    browser.preOpen(undefined)
  }

  const handleClick = async () => {
    const r = await run()
    if (Result.isFailure(r)) {
      browser.close()
      toast.error("Не удалось оформить подписку")
      return
    }
    const result = r.value
    if (!result) {
      browser.close()
      toast.error("Не удалось оформить подписку")
      return
    }
    if (result.kind === "activated") {
      browser.close()
      await queryClient.invalidateQueries()
      return
    }
    if (result.kind === "declined") {
      browser.close()
      toast.error(result.reason)
      await queryClient.invalidateQueries()
      return
    }
    browser.goTo(result.confirmationUrl)
    await queryClient.invalidateQueries({ queryKey: plansQueryKey })
  }

  return (
    <Button
      disabled={disabled || loading}
      onClick={handleClick}
      onMouseDown={(event) => { handlePreOpen(); onMouseDown?.(event) }}
      onTouchStart={(event) => { handlePreOpen(); onTouchStart?.(event) }}
      {...props}
    >
      {loading ? (
        <div className="flex items-center gap-s">{children} <ImSpinner2 className="animate-spin size-4" /></div>
      ) : children}
    </Button>
  )
}
